// ── WHAT ACREAGE IS THIS, AND MAY IT PRICE A PREMIUM? ────────────────────────
//
// The wheat quoter already establishes the measured property this rests on:
// the GUARANTEE and the COVERAGE are acre-invariant, and the PREMIUM is not.
// Acres enter RMA's rating in exactly one place — `discFactor`'s BU/EU discount
// band — so a quote may state coverage with no acreage at all and may never
// state a premium without one.
//
// The renewal advisor is the premium half by construction: it compares NET COST
// between coverage levels. So an unknown acreage is not a cosmetic gap there,
// it is the denominator of the decision.
//
// ⚠ MEASURED 2026-09-01, and the discount band is NOT the dominant term.
// NY LIVINGSTON 003, APH 74, $7.46, the 75 -> 80 move:
//
//     1 ac   BU   $9.00/ac        <- a nominal acre
//    19 ac   BU   $9.10/ac
//    20 ac   EU   $6.65/ac        <- the unit structure FLIPS
//   100 ac   EU   $6.52/ac        <- a fabricated default
//  1000 ac   EU   $6.32/ac
//
// The BU->EU step at 20 acres is worth 27%; the whole discount band from 100 to
// 1,000 acres is worth 3%. So an invented acreage does not merely nudge the
// premium, it invents a UNIT STRUCTURE — and it invents it in the direction
// that makes buying up look cheaper, which is the direction that costs a grower
// money on a document they sign.
//
// ── PRIOR-YEAR ACREAGE QUOTES, AND OUTRANKS THE INVENTED 100 (2026-09-02) ────
//
// Kevin: *"dont require acres to print quote, If i want to print a quote quick I
// should be able to. Default to last years or 100 acres."*
//
// This module used to refuse prior-year outright, on the argument that last
// year's ground does not price this year's premium and that a round 100 "reads
// as an assumption where 131.3 reads as a fact". The first half is true and the
// second is a LEGIBILITY problem, which a label solves — and refusing it meant
// the quoter fell through to a figure that is measurably WORSE.
//
// ⚠ MEASURED 2026-09-02, KAST FARMS (Orleans NY, EU 80%, real acreage 458.8):
//
//     priced on   1 ac   $15.00/ac   +2.5% against his own acreage
//               100 ac   $14.95      +2.2%      <- the invented default
//               458.8    $14.63       0.0%      <- his prior-year figure
//
// and across BU 12 ac through EU 900 ac the assumed 100 lands within about ±3%
// either way. So the choice is not "a fact versus an assumption", it is a figure
// that is usually EXACT versus one that is never exact — and the honest fix is
// to prefer the real one and SAY which year it came from.
//
// The 27% BU->EU step this file used to cite is real and is NOT this path: the
// quoter is handed the policy's own unit structure, so acres move the discount
// band alone. That figure belongs to the recommendation engine, which derives a
// structure, and nothing here changes for it.
//
// THE RULE THAT SURVIVES IS THE ONE THAT MATTERED: a premium may never rest on a
// figure the reader cannot identify. Every rung below REPORTED is disclosed on
// every surface that prints a dollar computed from it, and `source` says which.

export const ACREAGE_SOURCE = {
  REPORTED: "REPORTED",       // the AIP's own acreage report for THIS crop year
  PLANNED: "PLANNED",         // a figure a human typed for this crop year
  // ⚠ A STATED ASSUMPTION, NOT A MEASUREMENT — and it exists because a card of
  // dashes reads as a broken page rather than as a missing input, which is the
  // Metzler failure running the other way. The rule this repo refuses is a
  // SILENT default; a disclosed one is a different thing, and it must stay
  // disclosed on every surface that shows a dollar computed from it.
  //
  // It is deliberately a round 100 rather than the grower's prior-year acreage.
  // 100 reads as an assumption; 131.3 reads as a fact, and the whole point is
  // that the reader can tell the difference at a glance.
  ASSUMED: "ASSUMED",
  PRIOR_YEAR: "PRIOR_YEAR",   // reference only — never quotes
  // ⚠ THE UNIT'S OWN APH DATABASE — added 2026-09-23. Kevin: *"I want an optimizer for
  // all wheat customers based on their historical data."* `production_history` carries
  // ACRES per production year, per unit, and nothing was reading them as an acreage
  // basis. Measured on the live CY2027 wheat book: **192 of 214 units (90%)** have an
  // acreage in their own APH history, against 116 (54%) with a prior-year coverage
  // figure — and PRIOR_YEAR is a strict SUBSET of it (prior-only = 0 units).
  //
  // It ranks BELOW prior year, which is the same farm's more recent and more specific
  // figure, and ABOVE `ASSUMED`, which is a number nobody supplied.
  APH_HISTORY: "APH_HISTORY",
  UNKNOWN: "UNKNOWN",
};

// The prefill. One place, so the card, the print path and the checker cannot
// disagree about what was assumed.
export const DEFAULT_QUOTE_ACRES = 100;

export const ACREAGE_LABEL = {
  REPORTED: "Reported acreage",
  PLANNED: "Planned acreage (entered)",
  ASSUMED: "Assumed acreage — not reported",
  PRIOR_YEAR: "Last reported acreage — not yet reported for this year",
  APH_HISTORY: "Acreage from your APH history — not yet reported for this year",
  UNKNOWN: "No acreage on file",
};

// ⚠ WHICH SOURCES MAY FOUND A RECOMMENDATION, AND WHY IT IS NOT THE SAME SET AS
// "may print a premium".
//
// A printed premium is a number a grower signs against, so it is quoted only from
// REPORTED or PLANNED acres — a figure the AIP has or a human typed. That is the
// Metzler rule and it does not move.
//
// A coverage-level RECOMMENDATION is a different question: it compares the marginal
// cost of a band against its value, and BOTH sides of that comparison move together
// with acreage. Measured over 217 units at 50 / 200 / 500 / 1,000 acres — a 20x range —
// **216 of 217 recommend the identical level.** So any plausible real acreage answers
// the question; what does NOT answer it is a nominal acre (15 of 217 differ at 1 acre,
// every one of them biased LOW, because 1 acre sits at the top of the BU/EU discount
// band and makes coverage look dear) or an invented round 100.
//
// So: the farm's OWN historical acreage decides, an invented one never does.
export const DECIDES_COVERAGE = new Set([
  ACREAGE_SOURCE.REPORTED,
  ACREAGE_SOURCE.PLANNED,
  ACREAGE_SOURCE.PRIOR_YEAR,
  ACREAGE_SOURCE.APH_HISTORY,
]);

// True when a coverage change may be judged on this basis at all. ASSUMED and UNKNOWN
// are excluded deliberately — an assumed 100 is not this farm's history.
export const decidesCoverage = (basis) =>
  !!basis && DECIDES_COVERAGE.has(basis.source) && +basis.acres > 0;

const pos = (v) => (v != null && isFinite(+v) && +v > 0 ? +v : null);

// The one rule. Callers pass what they hold; this decides what may be priced.
export function resolveAcreageBasis({
  reportedAcres = null,
  plannedAcres = null,
  assumedAcres = null,
  priorYearAcres = null,
  priorYear = null,
  aphHistoryAcres = null,
  aphHistoryYear = null,
} = {}) {
  const rep = pos(reportedAcres), plan = pos(plannedAcres), prior = pos(priorYearAcres);
  const assumed = pos(assumedAcres), aph = pos(aphHistoryAcres);

  // Reference figures travel on the result whatever wins, so the screen can
  // show a grower what it knows without any of it becoming the quote basis.
  const reference = {
    reported: rep, planned: plan, priorYear: prior, priorYearOf: priorYear || null,
    aphHistory: aph, aphHistoryOf: aphHistoryYear || null,
  };

  if (rep != null) {
    return { acres: rep, source: ACREAGE_SOURCE.REPORTED, quotable: true,
             label: ACREAGE_LABEL.REPORTED, reference,
             detail: "the acreage reported to the AIP for this crop year" };
  }
  if (plan != null) {
    return { acres: plan, source: ACREAGE_SOURCE.PLANNED, quotable: true,
             label: ACREAGE_LABEL.PLANNED, reference,
             detail: "a planned figure entered for this crop year, not yet reported" };
  }
  // PRIOR YEAR outranks the invented default: it is the grower's own reported
  // figure and it is measurably closer than 100 on any farm that has one. It is
  // NOT this year's ground, so it is flagged `estimated` and every surface names
  // the year it came from — that disclosure is what makes it usable rather than
  // a premium quietly priced on land the grower may not plant again.
  if (prior != null) {
    return { acres: prior, source: ACREAGE_SOURCE.PRIOR_YEAR, quotable: true, estimated: true,
             label: ACREAGE_LABEL.PRIOR_YEAR, reference,
             detail: "no acreage is reported for this crop year yet, so the premium is quoted on the " +
                     prior + " acres last reported" + (priorYear ? " for " + priorYear : "") };
  }
  // THE UNIT'S OWN APH DATABASE. Ranks below PRIOR_YEAR — same farm, older figure —
  // and above ASSUMED. It is `estimated`, so it never prices a printed premium; what
  // it does is let the coverage comparison run at all, which on this book is the
  // difference between an optimizer that answers for 90% of wheat units and one that
  // answers for none. The year it came from travels with it and is named on screen.
  if (aph != null) {
    return { acres: aph, source: ACREAGE_SOURCE.APH_HISTORY, quotable: true, estimated: true,
             label: ACREAGE_LABEL.APH_HISTORY, reference,
             detail: "no acreage is reported for this crop year yet, so the coverage comparison uses the " +
                     aph + " acres in your APH history" + (aphHistoryYear ? " for " + aphHistoryYear : "") };
  }
  // ASSUMED ranks BELOW anything real and ABOVE nothing at all. It carries
  // `assumed: true` so no consumer has to compare strings to know a dollar
  // computed from it rests on a figure nobody supplied.
  if (assumed != null) {
    return { acres: assumed, source: ACREAGE_SOURCE.ASSUMED, quotable: true, assumed: true, estimated: true,
             label: ACREAGE_LABEL.ASSUMED, reference,
             detail: "no acreage is reported or on file for this unit, so the premium is quoted on an assumed " +
                     assumed + " acres — enter the real figure to price it properly" };
  }
  return { acres: null, source: ACREAGE_SOURCE.UNKNOWN, quotable: false,
           label: ACREAGE_LABEL.UNKNOWN, reference,
           detail: "no acreage has been reported or entered for this crop year" };
}

// ── THE FARM'S OWN MOST RECENT REPORTED ACREAGE, OUT OF ITS APH HISTORY ──────
// ⚠ MOVED HERE FROM App.jsx 2026-09-23 so there is ONE definition. The wheat card,
// the printed quote, the coverage recommendation and the book-run tools all need it,
// and a rule this repo copies is a rule this repo watches drift.
//
// It is the acreage the recommendation runs on when the quoted year has none — Kevin,
// 2026-09-23: *"You will not have reported acres for the quoted year, you can default
// to last years acres and allow us to type in estimated acres."*
//
// ⚠ THE YEAR TRAVELS WITH IT, because it is not necessarily last year's. DU MOND AG
// LLC's Wayne wheat is a Z (zero planted) record for 2026 and its 350 acres are from
// 2025; printed as "your 2026 acres" under a 2027 heading, that is one crop year's
// figure labelled as another.
export function wheatHistAcres(phRows, county) {
  const c = String(county || "").toUpperCase();
  const h = (phRows || []).filter(x => x.crop === "WHEAT" && (x.county || "").toUpperCase() === c && x.acres != null && +x.acres > 0)
    .sort((a, b) => b.crop_year - a.crop_year);
  return h.length ? { acres: +h[0].acres, year: h[0].crop_year != null ? +h[0].crop_year : null } : { acres: 0, year: null };
}

// What a premium-dependent surface gets back once acreage is supplied. Named
// rather than implied, so the screen can tell a grower what typing a number buys
// instead of just refusing.
export const UNLOCKED_BY_ACREAGE = [
  "net premium per acre at each coverage level",
  "the net cost of moving a coverage band",
  "the benefit/cost ratio a change is judged on",
  "the renewal recommendation itself",
];

// The coverage half is exact with no acreage at all, so the ladder is still
// quoted — at a NOMINAL acre, which is stated rather than hidden. Never use this
// for anything the premium touches.
export const NOMINAL_QUOTE_ACRES = 1;
